import { useEffect, useState, useCallback } from "react";
import {
  Plus, Trash, Wrench, ArrowUUpLeft, HandArrowDown, WarningCircle, Gauge, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard, MobileRow } from "@/components/Shared";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { CaptionedImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CONDITIONS = ["good", "worn", "damaged", "out_of_service"];
const FILTERS = [
  ["all", "All"],
  ["available", "In crib"],
  ["issued", "Issued"],
  ["overdue", "Overdue"],
  ["service_due", "Service due"],
];

const EMPTY_TOOL = {
  name: "", tool_code: "", category: "", location: "", condition: "good",
  calibration_due: "", maintenance_due: "", notes: "", image_id: null, image_caption: "",
};

const today = () => new Date().toISOString().slice(0, 10);
const isOverdue = (t) => t.issued_to_name && t.due_back && t.due_back < today();
const isServiceDue = (t) =>
  (t.calibration_due && t.calibration_due <= today()) ||
  (t.maintenance_due && t.maintenance_due <= today());

export default function Tools() {
  const { user } = useAuth();
  const [tools, setTools] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_TOOL);
  const [issueTarget, setIssueTarget] = useState(null);
  const [serviceTarget, setServiceTarget] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/tools", { params: { filter, search } });
    setTools(data);
  }, [filter, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user?.role === "admin") api.get("/users").then(({ data }) => setMembers(data)).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tools", {
        ...form,
        calibration_due: form.calibration_due || null,
        maintenance_due: form.maintenance_due || null,
      });
      toast.success("Tool added to the crib");
      setAddOpen(false);
      setForm(EMPTY_TOOL);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const checkin = async (tool) => {
    try {
      await api.post(`/tools/${tool.id}/checkin`, {});
      toast.success(`${tool.name} returned to the crib`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tools/${id}`);
      toast.success("Tool removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const issued = tools.filter((t) => t.issued_to_name).length;
  const overdue = tools.filter(isOverdue).length;
  const serviceDue = tools.filter(isServiceDue).length;

  const holderCell = (t) => {
    if (!t.issued_to_name) return <span className="text-xs text-slate-400">In crib</span>;
    return (
      <span className="text-xs">
        <span className="font-semibold text-[#0F172A]">{t.issued_to_name}</span>
        {t.due_back && (
          <span className={isOverdue(t) ? "block font-bold text-[#FF3B30]" : "block text-slate-500"}>
            due {t.due_back}
            {isOverdue(t) ? " · overdue" : ""}
          </span>
        )}
      </span>
    );
  };

  return (
    <div data-testid="tools-page">
      <PageHeader
        overline="Tool crib"
        title="Tools"
        description="Who has which tool right now, when it is due back, and what needs calibration or service."
        action={
          <Button
            data-testid="add-tool-button"
            onClick={() => setAddOpen(true)}
            className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto"
          >
            <Plus size={16} className="mr-2" /> Add tool
          </Button>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard testid="tools-stat-total" label="Tools in crib" value={tools.length} icon={Wrench} />
        <StatCard testid="tools-stat-issued" label="Issued out" value={issued} icon={HandArrowDown} tone="primary" />
        <StatCard testid="tools-stat-overdue" label="Overdue" value={overdue} icon={WarningCircle} tone="danger" sub="past due-back date" />
        <StatCard testid="tools-stat-service" label="Service due" value={serviceDue} icon={Gauge} tone="danger" sub="calibration or maintenance" />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={filter} onValueChange={setFilter} className="min-w-0">
          <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
            {FILTERS.map(([key, label]) => (
              <TabsTrigger key={key} data-testid={`tool-filter-${key}`} value={key} className="rounded-none text-xs uppercase tracking-wider">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="tools-search-input"
            placeholder="Search tools…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-none border-slate-300 pl-9"
          />
        </div>
      </div>

      {tools.length === 0 ? (
        <EmptyState testid="tools-empty" title="No tools here" description="Add a tool to the crib to start tracking who has it." />
      ) : (
        <>
          <div className="border border-border bg-white md:hidden" data-testid="tools-cards">
            {tools.map((t) => (
              <MobileRow
                key={t.id}
                testid={`tool-card-${t.id}`}
                title={t.name}
                badges={
                  <>
                    <StatusBadge testid={`tool-condition-${t.id}`} value={t.condition} />
                    {t.issued_to_name ? <StatusBadge value="ordered" /> : <StatusBadge value="approved" />}
                    {isOverdue(t) && <StatusBadge testid={`tool-overdue-${t.id}`} value="urgent" />}
                  </>
                }
                fields={[
                  { label: "Code", value: t.tool_code || "—" },
                  { label: "Location", value: t.location || "—" },
                  { label: "Holder", value: t.issued_to_name || "In crib" },
                  { label: "Due back", value: t.due_back || "—", tone: isOverdue(t) ? "danger" : undefined },
                  { label: "Calibration", value: t.calibration_due || "—", tone: t.calibration_due && t.calibration_due <= today() ? "danger" : undefined },
                  { label: "Maintenance", value: t.maintenance_due || "—", tone: t.maintenance_due && t.maintenance_due <= today() ? "danger" : undefined },
                ]}
                media={
                  <div className="mt-3 space-y-2">
                    <CaptionedImage
                      testid={`tool-image-${t.id}`}
                      imageId={t.image_id}
                      caption={t.image_caption}
                      className="h-40 w-full border border-border object-cover"
                    />
                    {t.issued_to_name ? (
                      <button
                        data-testid={`checkin-mobile-${t.id}`}
                        onClick={() => checkin(t)}
                        className="flex h-10 w-full items-center justify-center gap-2 border border-border text-xs font-semibold uppercase tracking-widest text-[#0B7A5B] active:bg-[#E6F7F1]"
                      >
                        <ArrowUUpLeft size={15} /> Check in
                      </button>
                    ) : (
                      <button
                        data-testid={`checkout-mobile-${t.id}`}
                        onClick={() => setIssueTarget(t)}
                        className="flex h-10 w-full items-center justify-center gap-2 border border-border text-xs font-semibold uppercase tracking-widest text-[#002FA7] active:bg-[#EEF2FF]"
                      >
                        <HandArrowDown size={15} /> Check out
                      </button>
                    )}
                    <button
                      data-testid={`service-mobile-${t.id}`}
                      onClick={() => setServiceTarget(t)}
                      className="flex h-10 w-full items-center justify-center gap-2 border border-border text-xs font-semibold uppercase tracking-widest text-slate-600 active:bg-slate-100"
                    >
                      <Gauge size={15} /> Service dates
                    </button>
                  </div>
                }
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto border border-border bg-white md:block">
            <table className="w-full min-w-[900px] text-sm" data-testid="tools-table">
              <thead>
                <tr className="border-b border-border bg-slate-50 text-left">
                  {["Tool", "Code", "Condition", "Location", "Holder / due back", "Calibration", "Maintenance", "Action"].map((h) => (
                    <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`tool-row-${t.id}`}
                    className={`border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50 ${
                      isOverdue(t) ? "bg-[#FFF7F6]" : ""
                    }`}
                  >
                    <td className="px-3 py-2.5 font-medium text-[#0F172A]">
                      <div className="flex items-center gap-2.5">
                        <CaptionedImage
                          testid={`tool-thumb-${t.id}`}
                          imageId={t.image_id}
                          caption={null}
                          className="h-9 w-9 shrink-0 border border-border object-cover"
                        />
                        <span>
                          {t.name}
                          {t.image_caption && (
                            <span className="block text-xs font-normal italic text-slate-500">
                              {t.image_caption}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{t.tool_code || "—"}</td>
                    <td className="px-3 py-2.5"><StatusBadge testid={`tool-condition-${t.id}`} value={t.condition} /></td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{t.location || "—"}</td>
                    <td className="px-3 py-2.5">{holderCell(t)}</td>
                    <td className={`px-3 py-2.5 font-mono text-xs ${t.calibration_due && t.calibration_due <= today() ? "font-bold text-[#FF3B30]" : "text-slate-500"}`}>
                      {t.calibration_due || "—"}
                    </td>
                    <td className={`px-3 py-2.5 font-mono text-xs ${t.maintenance_due && t.maintenance_due <= today() ? "font-bold text-[#FF3B30]" : "text-slate-500"}`}>
                      {t.maintenance_due || "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {t.issued_to_name ? (
                          <button
                            data-testid={`checkin-${t.id}`}
                            onClick={() => checkin(t)}
                            title="Check in"
                            className="border border-border p-1.5 text-[#0B7A5B] transition-colors duration-150 hover:bg-[#E6F7F1]"
                          >
                            <ArrowUUpLeft size={14} />
                          </button>
                        ) : (
                          <button
                            data-testid={`checkout-${t.id}`}
                            onClick={() => setIssueTarget(t)}
                            title="Check out"
                            className="border border-border p-1.5 text-[#002FA7] transition-colors duration-150 hover:bg-[#EEF2FF]"
                          >
                            <HandArrowDown size={14} />
                          </button>
                        )}
                        <button
                          data-testid={`service-${t.id}`}
                          onClick={() => setServiceTarget(t)}
                          title="Service dates"
                          className="border border-border p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-100"
                        >
                          <Gauge size={14} />
                        </button>
                        {user?.role === "admin" && (
                          <button
                            data-testid={`delete-tool-${t.id}`}
                            onClick={() => remove(t.id)}
                            title="Remove tool"
                            className="border border-border p-1.5 text-[#FF3B30] transition-colors duration-150 hover:bg-[#FFECEB]"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add tool */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Add tool</DialogTitle>
            <DialogDescription>Register a tool so it can be issued and serviced.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-tool-form">
            <div>
              <Label className="overline text-slate-500">Tool name</Label>
              <Input data-testid="tool-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Tool code</Label>
                <Input data-testid="tool-code-input" value={form.tool_code} onChange={(e) => setForm({ ...form, tool_code: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Category</Label>
                <Input data-testid="tool-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="hand tool, measuring…" className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Location</Label>
                <Input data-testid="tool-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Condition</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger data-testid="tool-condition-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Calibration due</Label>
                <Input data-testid="tool-calibration-input" type="date" value={form.calibration_due} onChange={(e) => setForm({ ...form, calibration_due: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Maintenance due</Label>
                <Input data-testid="tool-maintenance-input" type="date" value={form.maintenance_due} onChange={(e) => setForm({ ...form, maintenance_due: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Notes</Label>
              <Textarea data-testid="tool-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <ImageCaptionField
              testid="tool-image"
              value={form.image_id}
              caption={form.image_caption}
              onChange={({ image_id, image_caption }) => setForm((f) => ({ ...f, image_id, image_caption }))}
            />
            <DialogFooter>
              <Button data-testid="save-tool-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Add tool
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {issueTarget && (
        <CheckoutDialog
          key={issueTarget.id}
          tool={issueTarget}
          members={members}
          onClose={() => setIssueTarget(null)}
          onSaved={load}
        />
      )}
      {serviceTarget && (
        <ServiceDialog
          key={serviceTarget.id}
          tool={serviceTarget}
          onClose={() => setServiceTarget(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

const CheckoutDialog = ({ tool, members, onClose, onSaved }) => {
  const [form, setForm] = useState({
    issued_to_user_id: "",
    issued_to_name: "",
    due_back: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/tools/${tool.id}/checkout`, {
        ...form,
        issued_to_user_id: form.issued_to_user_id || null,
        due_back: form.due_back || null,
      });
      toast.success(`${tool.name} issued`);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Check out tool</DialogTitle>
          <DialogDescription>{tool.name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="checkout-form">
          <div>
            <Label className="overline text-slate-500">Issue to a member</Label>
            <Select
              value={form.issued_to_user_id}
              onValueChange={(v) => setForm({ ...form, issued_to_user_id: v, issued_to_name: "" })}
            >
              <SelectTrigger data-testid="checkout-member-select" className="mt-1.5 rounded-none border-slate-300">
                <SelectValue placeholder="Pick a member" />
              </SelectTrigger>
              <SelectContent>
                {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-slate-500">Or type a name / employee ID</Label>
            <Input
              data-testid="checkout-name-input"
              value={form.issued_to_name}
              onChange={(e) => setForm({ ...form, issued_to_name: e.target.value, issued_to_user_id: "" })}
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>
          <div>
            <Label className="overline text-slate-500">Due back</Label>
            <Input
              data-testid="checkout-due-input"
              type="date"
              value={form.due_back}
              onChange={(e) => setForm({ ...form, due_back: e.target.value })}
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>
          <div>
            <Label className="overline text-slate-500">Note</Label>
            <Input
              data-testid="checkout-note-input"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="save-checkout-button"
              type="submit"
              disabled={busy}
              className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto"
            >
              {busy ? "Issuing…" : "Issue tool"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ServiceDialog = ({ tool, onClose, onSaved }) => {
  const [form, setForm] = useState({
    calibration_due: tool.calibration_due || "",
    maintenance_due: tool.maintenance_due || "",
    condition: tool.condition || "good",
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/tools/${tool.id}/service`, {
        ...form,
        calibration_due: form.calibration_due || null,
        maintenance_due: form.maintenance_due || null,
      });
      toast.success("Service details updated");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Calibration & maintenance</DialogTitle>
          <DialogDescription>{tool.name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="service-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="overline text-slate-500">Calibration due</Label>
              <Input data-testid="service-calibration-input" type="date" value={form.calibration_due} onChange={(e) => setForm({ ...form, calibration_due: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">Maintenance due</Label>
              <Input data-testid="service-maintenance-input" type="date" value={form.maintenance_due} onChange={(e) => setForm({ ...form, maintenance_due: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
          </div>
          <div>
            <Label className="overline text-slate-500">Condition</Label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger data-testid="service-condition-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-slate-500">Note</Label>
            <Input data-testid="service-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
          </div>
          <DialogFooter>
            <Button
              data-testid="save-service-button"
              type="submit"
              disabled={busy}
              className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
